import { materialTypeFormat } from '@/common/material-type-formatting';
import { ContentPanel } from '@/components/content-panel';
import { Card, Flex, Grid, LoadingOverlay, Text, Title } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { motion } from 'motion/react';
import { MaterialType } from '../../../../../backend/src/types.generated';
import { useUserScanMaterialConfirmMutation } from '../graphql.generated';
import { useBagScanContext } from '../hook';
import styles from '../styles.module.css';

export const BagScanMaterialConfirmPage = () => {
	const { setPage, setScannedBag, scannedBag } = useBagScanContext();
	const [userScanMaterialConfirmMutation, { loading }] = useUserScanMaterialConfirmMutation({
		onCompleted: (data) => {
			if (data.confirmBagMaterial?.material) {
				setScannedBag((prev) =>
					prev
						? {
								...prev,
								material: data.confirmBagMaterial!.material,
							}
						: prev
				);
			}
			setPage('RESULT');
		},
	});

	/**
	 * TODO:
	 * Eventually, we will want a way of determining which material types are
	 * user facing. Right now, the only 2 types that are available are these.
	 */
	const materialTypes = [MaterialType.LiquidPaperBoard, MaterialType.SoftPlastic]
		.map((materialType) => {
			const config = materialTypeFormat[materialType];
			return {
				name: config.label,
				value: materialType,
				emoji: config.emoji,
			};
		})
		.reverse();

	const handleMaterialConfirm = (material: MaterialType) => {
		if (scannedBag?.id) {
			userScanMaterialConfirmMutation({
				variables: { input: { id: scannedBag.id, material } },
			});
		} else {
			setPage('RESULT');
		}
	};

	return (
		<Flex className={styles.materialConfirmContainer}>
			<motion.div
				initial={{ opacity: 0, y: -50 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5, ease: 'easeOut' }}
				className={styles.materialConfirmHeader}
			>
				<IconArrowLeft onClick={() => setPage('INSTRUCTIONS')} />
				<Title order={2}>Confirm Material</Title>
			</motion.div>
			<LoadingOverlay zIndex={400} visible={loading} />
			<ContentPanel gap="sm">
				<motion.div
					initial={{ opacity: 0, scale: 0.8 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
				>
					<Flex direction="column" className={styles.entries}>
						<Title order={2} ta="center">
							What material is in this bag?
						</Title>
					</Flex>
				</motion.div>
				<Grid gutter="sm">
					{materialTypes.map((materialType, index) => (
						<Grid.Col key={materialType.value} span={6}>
							<motion.div
								style={{ height: '100%' }}
								initial={{ opacity: 0, y: 20, scale: 0.9 }}
								animate={{ opacity: 1, y: 0, scale: 1 }}
								transition={{
									duration: 0.4,
									delay: 0.4 + index * 0.1,
									ease: 'easeOut',
								}}
							>
								<Card
									shadow="sm"
									padding="sm"
									radius="md"
									withBorder
									className={styles.materialTypeCard}
									onClick={() => handleMaterialConfirm(materialType.value)}
								>
									<Flex direction="column" align="center" gap="xs">
										<Text size="1.5rem" className={styles.materialEmojiText}>
											{materialType.emoji}
										</Text>
										<Text size="xs" ta="center" fw={500}>
											{materialType.name}
										</Text>
									</Flex>
								</Card>
							</motion.div>
						</Grid.Col>
					))}
				</Grid>
			</ContentPanel>
		</Flex>
	);
};
